import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, MenuItem,
  Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ScienceIcon from '@mui/icons-material/Science';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { request } from './OperationsProvider';

// Tyre monitoring, from the platform's side: which vehicles are configured, and the simulator.
//
// The simulator is here rather than on a customer's screen because switching a lorry's tyre
// readings from real telemetry to invented ones is a decision with consequences, and it belongs to
// whoever runs the platform. Everything it produces goes through the same engine as real telemetry,
// so what is demonstrated here is the product, not a mock-up of it.
const SCENARIOS = [
  ['NORMAL', 'Normal'],
  ['LOW_PRESSURE', 'Low pressure'],
  ['CRITICAL_LOW_PRESSURE', 'Critically low pressure'],
  ['HIGH_PRESSURE', 'Over pressure'],
  ['HIGH_TEMPERATURE', 'Running hot'],
  ['SLOW_LEAK', 'Slow leak'],
  ['RAPID_PRESSURE_LOSS', 'Rapid pressure loss'],
  ['SENSOR_OFFLINE', 'Sensor offline'],
  ['STALE_SENSOR', 'Sensor stopped transmitting'],
  ['LOW_BATTERY', 'Sensor battery low'],
  ['RECOVERY', 'Repaired'],
];

const PlatformTpms = ({ tenants }) => {
  const [layouts, setLayouts] = useState([]);
  const [sims, setSims] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [pending, setPending] = useState(false);
  const [form, setForm] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [position, setPosition] = useState('');
  const [scenario, setScenario] = useState('SLOW_LEAK');

  const load = async () => {
    const [l, s] = await Promise.all([
      request('/api/platform/tpms/layouts'),
      request('/api/platform/tpms/simulation'),
    ]);
    setLayouts(l); setSims(s);
  };
  useEffect(() => { load().catch((e) => setError(e.message)); }, []);

  const tenantName = useMemo(() => {
    const byId = new Map((tenants || []).map((t) => [String(t.id), t.name]));
    return (id) => byId.get(String(id)) || id;
  }, [tenants]);

  const act = async (path, body, success) => {
    setPending(true); setError(null);
    try {
      await request(path, body === null ? 'POST' : 'POST', body);
      if (success) setNotice(success);
      await load();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  const configure = async (event) => {
    event.preventDefault();
    setPending(true); setError(null);
    try {
      await request('/api/platform/tpms/config', 'PUT', {
        workspaceId: form.workspaceId,
        vehicleId: Number(form.vehicleId),
        layoutName: form.layoutName,
        enabled: 'true',
      });
      await request('/api/platform/tpms/simulation/start', 'POST', {
        workspaceId: form.workspaceId,
        vehicleId: Number(form.vehicleId),
        targetPressure: Number(form.targetPressure) || 105,
        intervalMs: Number(form.intervalSeconds || 10) * 1000,
        force: form.force ? 'true' : 'false',
      });
      setForm(null);
      setNotice('Simulation started. Readings appear on the customer’s tyre screens within a tick.');
      await load();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  const positions = chosen ? Object.keys(chosen.tyres || {}).sort() : [];

  return (
    <Box sx={{ mt: 5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Typography variant="h6">Tyre monitoring</Typography>
        <Button variant="contained" startIcon={<ScienceIcon />} onClick={() => { setError(null); setForm({ workspaceId: String(tenants?.[0]?.id || ''), vehicleId: '', layoutName: layouts[0]?.name || '', targetPressure: 105, intervalSeconds: 10 }); }}>
          Simulate a vehicle
        </Button>
      </Stack>
      <Typography color="text.secondary" sx={{ my: 2 }}>
        Simulated readings run through the same monitoring rules as real telemetry, so alerts raised here are real alerts.
      </Typography>

      {error && !form && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ my: 1 }}>{notice}</Alert>}

      <Stack gap={2}>
        {!sims.length && <Alert severity="info">No vehicle is being simulated.</Alert>}
        {sims.map((sim) => (
          <Paper key={`${sim.workspaceId}:${sim.vehicleId}`} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Box>
                <Typography fontWeight={600}>{`${tenantName(sim.workspaceId)} · vehicle ${sim.vehicleId}`}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {`${sim.layoutName} · target ${sim.targetPressure} PSI · every ${Math.round(sim.intervalMs / 1000)}s`}
                </Typography>
              </Box>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Chip size="small" label={sim.enabled ? 'Running' : 'Stopped'} color={sim.enabled ? 'success' : 'default'} />
                <Tooltip title="Put every tyre back to healthy">
                  <span>
                    <Button size="small" startIcon={<RestartAltIcon />} disabled={pending} onClick={() => act('/api/platform/tpms/simulation/reset', { workspaceId: sim.workspaceId, vehicleId: sim.vehicleId }, 'Every tyre reset.')}>Reset all</Button>
                  </span>
                </Tooltip>
                {sim.enabled
                  ? <Button size="small" color="error" startIcon={<StopIcon />} disabled={pending} onClick={() => act('/api/platform/tpms/simulation/stop', { workspaceId: sim.workspaceId, vehicleId: sim.vehicleId }, 'Simulation stopped.')}>Stop</Button>
                  : <Button size="small" startIcon={<PlayArrowIcon />} disabled={pending} onClick={() => act('/api/platform/tpms/simulation/start', { workspaceId: sim.workspaceId, vehicleId: sim.vehicleId }, 'Simulation started.')}>Start</Button>}
              </Stack>
            </Stack>

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
              <TextField
                size="small"
                select
                label="Tyre"
                sx={{ minWidth: 130 }}
                value={chosen === sim ? position : ''}
                onChange={(e) => { setChosen(sim); setPosition(e.target.value); }}
              >
                {Object.keys(sim.tyres || {}).sort().map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
              <TextField size="small" select label="Condition" sx={{ minWidth: 220 }} value={scenario} onChange={(e) => setScenario(e.target.value)}>
                {SCENARIOS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
              <Button
                size="small"
                variant="outlined"
                disabled={pending || chosen !== sim || !position}
                onClick={() => request('/api/platform/tpms/simulation/tyre', 'PATCH', { workspaceId: sim.workspaceId, vehicleId: sim.vehicleId, position, scenario })
                  .then(() => { setNotice(`${position} set to ${scenario.replaceAll('_', ' ').toLowerCase()}.`); return load(); })
                  .catch((e) => setError(e.message))}
              >
                Apply
              </Button>
              <Button
                size="small"
                disabled={pending || chosen !== sim || !position}
                onClick={() => act('/api/platform/tpms/simulation/reset', { workspaceId: sim.workspaceId, vehicleId: sim.vehicleId, position }, `${position} reset.`)}
              >
                Reset tyre
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={pending || chosen !== sim || !position}
                onClick={() => act('/api/platform/tpms/simulation/scenario', { workspaceId: sim.workspaceId, vehicleId: sim.vehicleId, position }, 'Demonstration running: the tyre will leak, warn, go critical, then be repaired.')}
              >
                Run demonstration
              </Button>
            </Stack>
            {positions.length > 0 && chosen === sim && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                The demonstration walks the chosen tyre down through a warning to a critical alert, then repairs it and lets the alert recover.
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>

      <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={configure}>
          <DialogTitle>Simulate a vehicle&apos;s tyres</DialogTitle>
          <DialogContent>
            <Stack gap={2} sx={{ mt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField select required label="Customer" value={form?.workspaceId || ''} onChange={(e) => setForm((f) => ({ ...f, workspaceId: e.target.value }))}>
                {(tenants || []).map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
              </TextField>
              <TextField
                required
                label="Vehicle id"
                helperText="The vehicle in that customer's register that these tyres belong to."
                value={form?.vehicleId || ''}
                onChange={(e) => setForm((f) => ({ ...f, vehicleId: e.target.value }))}
              />
              <TextField select required label="Tyre layout" value={form?.layoutName || ''} onChange={(e) => setForm((f) => ({ ...f, layoutName: e.target.value }))}>
                {layouts.map((l) => <MenuItem key={l.name} value={l.name}>{l.name}</MenuItem>)}
              </TextField>
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                <TextField fullWidth type="number" label="Target pressure (PSI)" value={form?.targetPressure ?? 105} onChange={(e) => setForm((f) => ({ ...f, targetPressure: e.target.value }))} />
                <TextField fullWidth type="number" label="Reading every (seconds)" value={form?.intervalSeconds ?? 10} onChange={(e) => setForm((f) => ({ ...f, intervalSeconds: e.target.value }))} />
              </Stack>
              <TextField
                select
                label="If the vehicle is on real telemetry"
                helperText="A lorry reporting real tyre readings is not replaced with invented ones unless you say so here."
                value={form?.force ? 'yes' : 'no'}
                onChange={(e) => setForm((f) => ({ ...f, force: e.target.value === 'yes' }))}
              >
                <MenuItem value="no">Leave it alone</MenuItem>
                <MenuItem value="yes">Take it over for this demonstration</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setForm(null)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={pending}>Start</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default PlatformTpms;
